import KuikaSkeleton from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import "./helpdesk-skeleton.scss";

/**
 * Helpdesk first-load placeholders.
 *
 * The Helpdesk used to answer its three fetches with `KuikaSpinnerSmall`: a centred logo over
 * an empty canvas while the list loaded (the ticket rail rendered literally nothing next to
 * it), and — on a row click — the same overlay at 50% opacity over the PREVIOUS ticket, which
 * reads as "nothing happened" for the whole second the detail is in flight. These stand in for
 * the shape that is coming instead, so a slow load reads as progress.
 *
 * Bars are the shared `KuikaSkeleton` primitive (it owns shimmer + reduced-motion); this file
 * only holds the helpdesk-specific compositions, and each block carries `role="status"` so the
 * region announces the load once while the bars stay `aria-hidden`.
 */

/** Ticket-list geometry, mirrored from `.zendesk-ticket-item` so the swap costs no reflow. */
const TITLE_HEIGHT: number = 20;

const LINE_HEIGHT: number = 17;

const FOOTER_HEIGHT: number = 16;

interface IHelpdeskSkeletonGroup {
  label: string;
  rows: readonly string[];
}

/**
 * Two groups, because that is what a real inbox opens on: Open expanded over a collapsed
 * Solved. The per-row widths vary so the stack reads as subjects rather than as a grid.
 */
const LIST_GROUPS: readonly IHelpdeskSkeletonGroup[] = [
  { label: "open", rows: ["78%", "64%", "85%", "58%"] },
  { label: "solved", rows: ["71%", "88%", "62%"] }
];

export const TicketListSkeleton = (): JSX.Element => (
  <div className="kuika-helpdesk-skeleton__list" role="status" aria-label="Loading tickets">
    {LIST_GROUPS.map((group: IHelpdeskSkeletonGroup) => (
      <div key={group.label} className="kuika-helpdesk-skeleton__list-group">
        <div className="kuika-helpdesk-skeleton__list-header">
          <KuikaSkeleton width={64} height={12} />
          <KuikaSkeleton width={20} height={12} />
        </div>
        {group.rows.map((width: string, index: number) => (
          <div key={`${group.label}-${index}`} className="kuika-helpdesk-skeleton__row">
            <div className="kuika-helpdesk-skeleton__row-title">
              <KuikaSkeleton width={width} height={TITLE_HEIGHT} />
              <KuikaSkeleton width={38} height={TITLE_HEIGHT} />
            </div>
            <KuikaSkeleton width="100%" height={LINE_HEIGHT} />
            <KuikaSkeleton width="72%" height={LINE_HEIGHT} />
            <div className="kuika-helpdesk-skeleton__row-footer">
              <KuikaSkeleton width={54} height={FOOTER_HEIGHT} />
              <KuikaSkeleton className="kuika-helpdesk-skeleton__row-date" width={62} height={FOOTER_HEIGHT} />
            </div>
          </div>
        ))}
      </div>
    ))}
  </div>
);

/** One thread message: avatar + author + date, then the body. Mirrors `.ticket-comment`. */
const THREAD_BODIES: readonly (readonly string[])[] = [
  ["96%", "88%", "91%", "54%"],
  ["92%", "67%"],
  ["89%", "94%", "48%"]
];

export const TicketThreadSkeleton = (): JSX.Element => (
  <div className="kuika-helpdesk-skeleton__thread" role="status" aria-label="Loading conversation">
    {THREAD_BODIES.map((lines: readonly string[], index: number) => (
      <div key={`m${index}`} className="kuika-helpdesk-skeleton__message">
        <div className="kuika-helpdesk-skeleton__message-header">
          <KuikaSkeleton className="kuika-helpdesk-skeleton__avatar" width={24} height={24} />
          <KuikaSkeleton width={132} height={LINE_HEIGHT} />
          <KuikaSkeleton className="kuika-helpdesk-skeleton__message-date" width={72} height={12} />
        </div>
        <div className="kuika-helpdesk-skeleton__message-body">
          {lines.map((width: string, line: number) => (
            <KuikaSkeleton key={`m${index}-${line}`} width={width} height={LINE_HEIGHT} />
          ))}
        </div>
      </div>
    ))}
  </div>
);

/**
 * The whole reading pane: the ticket card's header over the thread. Used both for the very
 * first open (no ticket chosen yet) and for a ticket SWITCH, where showing the outgoing
 * ticket's thread under a dimming overlay was the "nothing is happening" bug.
 */
export const TicketDetailSkeleton = (): JSX.Element => (
  <div className="kuika-helpdesk-skeleton__detail" role="status" aria-label="Loading ticket">
    <div className="kuika-helpdesk-skeleton__detail-card">
      <div className="kuika-helpdesk-skeleton__detail-header">
        <div className="kuika-helpdesk-skeleton__detail-subject">
          <KuikaSkeleton width="52%" height={22} />
          <div className="kuika-helpdesk-skeleton__detail-badges">
            <KuikaSkeleton width={48} height={18} />
            <KuikaSkeleton width={62} height={18} />
            <KuikaSkeleton width={78} height={12} />
          </div>
        </div>
        <KuikaSkeleton width="38%" height={12} />
      </div>
      <TicketThreadSkeleton />
    </div>
  </div>
);

interface IHelpdeskBoardSkeletonColumn {
  status: string;
  cards: number;
}

const BOARD_COLUMNS: readonly IHelpdeskBoardSkeletonColumn[] = [
  { status: "open", cards: 3 },
  { status: "pending", cards: 1 },
  { status: "solved", cards: 2 }
];

export const HelpdeskBoardSkeleton = (): JSX.Element => (
  <div className="kuika-helpdesk-skeleton__board" role="status" aria-label="Loading board">
    {BOARD_COLUMNS.map((column: IHelpdeskBoardSkeletonColumn) => (
      <div key={column.status} className="kuika-helpdesk-skeleton__board-column">
        <div className="kuika-helpdesk-skeleton__board-header">
          <KuikaSkeleton className="kuika-helpdesk-skeleton__board-dot" width={13} height={13} />
          <KuikaSkeleton width={58} height={12} />
          <KuikaSkeleton width={18} height={12} />
        </div>
        {Array.from({ length: column.cards }, (_unused: unknown, index: number) => (
          <div key={`${column.status}-${index}`} className="kuika-helpdesk-skeleton__board-card">
            <KuikaSkeleton width="68%" height={TITLE_HEIGHT} />
            <KuikaSkeleton width="100%" height={LINE_HEIGHT} />
            <KuikaSkeleton width="46%" height={FOOTER_HEIGHT} />
          </div>
        ))}
      </div>
    ))}
  </div>
);
